/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Pagination from 'react-js-pagination';
import baseUrl from '../BaseUrl';
import { watchParam } from '../../context/UserSegmentContext';
import ExportButton from './ExportButton';
import EmptyState from './states/EmptyState';
import ErrorState from './states/ErrorState';

/**
 * Drill-down popup for the Daily-breakdown counts (feature 053). Lists the
 * individual active users behind a clicked cell — Name · Email · App version ·
 * Date — reconciling with the count, honoring the page's watch/all segment.
 * Paginated + CSV export. The app version shown is each user's CURRENT version.
 *
 * Props: { open, ctx:{date, platform, count}, segment, onClose }
 */
const LIMIT = 20;

const PLATFORM_LABELS = {
  android: 'Android',
  ios: 'iOS',
  android_watch: 'Android watch',
  ios_watch: 'iOS watch',
  total: 'All',
};

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'app_version', label: 'App version' },
  { key: 'access_date', label: 'Date (UTC)' },
];

const asDate = (d) => (d ? String(d).split('-').reverse().join('/') : '-');

const SUMMARY_TOP_N = 8; // versions shown individually in the in-popup breakdown

const ActiveUsersVersionModal = ({ open, ctx, segment, onClose }) => {
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      const now = new Date();
      if (expire * 1000 < now.getTime()) {
        const r = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${r.data.accessToken}`;
        setToken(r.data.accessToken);
        setExpire(jwtDecode(r.data.accessToken).exp);
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  const buildUrl = useCallback(
    (pg, exportAll) => {
      const parts = [`date=${ctx.date}`, `platform=${ctx.platform}`];
      if (exportAll) {
        parts.push('export=1');
      } else {
        parts.push(`page=${pg}`, `limit=${LIMIT}`);
      }
      return `${baseUrl}/admin/api/get-active-users-version-detail?${parts.join('&')}${watchParam(segment)}`;
    },
    [ctx, segment]
  );

  const load = useCallback(
    async (pg) => {
      if (!ctx) return;
      setLoading(true);
      setError('');
      try {
        const res = await axiosJWT.get(buildUrl(pg, false));
        setRows(res.data.data || []);
        setTotalItem(res.data.totalItem || 0);
        setSummary(res.data.versionSummary || []);
      } catch {
        setError('We couldn’t load the users for this cell. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [ctx, buildUrl]
  );

  useEffect(() => {
    if (open && ctx) {
      setPage(1);
      load(1);
    }
  }, [open, ctx, segment]);

  if (!open || !ctx) return null;

  const handlePage = (pg) => {
    setPage(pg);
    load(pg);
  };

  const fetchExportRows = async () => {
    const res = await axiosJWT.get(buildUrl(1, true));
    return res.data.data || [];
  };

  const label = PLATFORM_LABELS[ctx.platform] || ctx.platform;
  const exportName = `active-users-${ctx.platform}-${ctx.date}.csv`;

  // In-popup version breakdown for this exact cell (consolidate the long tail).
  const summaryTotal = summary.reduce((s, r) => s + (Number(r.users) || 0), 0);
  let summaryDisplay = summary;
  if (summary.length > SUMMARY_TOP_N) {
    const head = summary.slice(0, SUMMARY_TOP_N);
    const tail = summary.slice(SUMMARY_TOP_N);
    const otherUsers = tail.reduce((s, r) => s + (Number(r.users) || 0), 0);
    summaryDisplay = [...head, { version: `Older / other (${tail.length})`, users: otherUsers, _other: true }];
  }
  const summaryMax = summaryDisplay.reduce((m, r) => Math.max(m, Number(r.users) || 0), 0) || 1;

  return (
    <div className="au-modal-bg" data-testid="auv-modal" onClick={onClose}>
      <div className="au-modal au-modal--wide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="au-modal-close" onClick={onClose} aria-label="Close" title="Close">×</button>
        <h2 className="au-modal-title">{label} active users · {asDate(ctx.date)}</h2>
        <p className="auv-caption">App version shown is each user’s current app version (not the version used on this day).</p>

        {!loading && !error && summaryDisplay.length > 0 && (
          <div className="auv-modal-summary" data-testid="auv-modal-summary">
            <div className="auv-modal-summary__title">Version breakdown</div>
            <ul className="auv-modal-summary__list">
              {summaryDisplay.map((r, i) => {
                const users = Number(r.users) || 0;
                const pct = summaryTotal > 0 ? Math.round((users / summaryTotal) * 100) : 0;
                const barPct = Math.round((users / summaryMax) * 100);
                return (
                  <li className="auv-modal-summary__row" key={r.version || i}>
                    <span className="auv-modal-summary__label" title={r.version}>{r.version}</span>
                    <span className="auv-modal-summary__bar-wrap" aria-hidden="true">
                      <span className={`auv-modal-summary__bar${r._other ? ' auv-dist-bar--other' : ''}`} style={{ width: `${barPct}%` }} />
                    </span>
                    <span className="auv-modal-summary__count tnum">{users.toLocaleString()}</span>
                    <span className="auv-dist-pct tnum">{pct}%</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="auv-modal-toolbar">
          <span className="auv-count">{Number(totalItem || ctx.count || 0).toLocaleString()} users</span>
          <span className="au-spacer" />
          <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExportRows} filename={exportName} />
        </div>

        {loading ? (
          <p className="au-modal-empty">Loading…</p>
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(page)} />
        ) : rows.length === 0 ? (
          <EmptyState title="No users" message="No users found for this cell." />
        ) : (
          <>
            <table className="au-modal-table" data-testid="auv-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>App version</th><th>Date (UTC)</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id ?? i}>
                    <td data-label="Name">{r.name || '-'}</td>
                    <td data-label="Email">{r.email || '-'}</td>
                    <td data-label="App version" className="au-num-blue">{r.app_version || 'Unknown'}</td>
                    <td data-label="Date (UTC)">{asDate(r.access_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalItem > LIMIT && (
              <div className="auv-pagination">
                <Pagination
                  activePage={page}
                  itemsCountPerPage={LIMIT}
                  totalItemsCount={totalItem}
                  pageRangeDisplayed={5}
                  onChange={handlePage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActiveUsersVersionModal;
